import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../../redux/store';
import {
  Search,
  Trash2,
  AlertCircle,
  Clock,
  Calendar,
  Plus,
  Menu,
  Package,
  Bell,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import { TaskyHelpLink } from '../../components/tasky/layout/TaskyHeaderActions';
import SimpleModal from '../../components/common/SimpleModal';
import { useProducts } from '../../hooks/useProducts';
import { Product, ProductImage } from '../../types/product';
import ProductCreateForm from './components/ProductCreateForm';
import ProductRegistrationModal from '../../components/product/ProductRegistrationModal';
import KnowledgeFetchProgress from './components/KnowledgeFetchProgress';
import KnowledgeOnboardingGuideModal, {
  shouldShowOnboardingGuide,
} from './components/KnowledgeOnboardingGuideModal';
import { selectedServiceFromProductParam } from './selectedServiceFromProductParam';
import { knowledgeTabFromParam } from './knowledgeTabFromParam';
import DeleteConfirmDialog from '../../components/knowledge/DeleteConfirmDialog';
import NotificationSettingsModal from '../../components/knowledge/NotificationSettingsModal';
import NotificationRecipientAvatars from '../../components/knowledge/NotificationRecipientAvatars';
import { NotificationRecipient } from '../../types/product';
import {
  ResponsiveAppShell,
  TaskyButton,
  TaskyErrorState,
  TaskySpinner,
  TaskyTabs,
  useConfirm,
  type TaskyTabItem,
} from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import { useProductRefetch } from '../../hooks/useProductRefetch';

// Tab Components
import CompetitorTab from './tabs/CompetitorTab';
// 資料タブは一旦非表示（復活時はコメントを外す）
// import MaterialsTab from './tabs/MaterialsTab';
import APIIntegrationTab from './tabs/APIIntegrationTab';
import ProductAdAccountsTab from './tabs/ProductAdAccountsTab';
import ProductImagesTab from './tabs/ProductImagesTab';
import StrategyMapTab from './tabs/StrategyMapTab';
import BasicInfoTab from './tabs/BasicInfoTab';
import BrandGuidelineSection from '../../components/knowledge/sections/BrandGuidelineSection';
import { BrandGuideline } from '../../types/product';
import productService from '../../services/api/productService';

interface ServiceData {
  id: string;
  name: string;
  category: string;
  status: 'active' | 'inactive' | 'fetching' | 'failed' | 'discontinued';
  lastUpdated: string;
  productData?: {
    id: number;
    status?: Product['status'];
    url?: string;
    description?: string;
    price: number;
    salePrice?: number;
    currency: string;
    stockQuantity: number;
    images?: any[];
    product_images?: ProductImage[]; // Knowledge\ProductImageテーブルからロードされる画像
    tags?: string[];
    metadata?: any;
    extended_metadata?: any;
  };
  upsider?: {
    description: string;
    features: string[];
    basicInfo?: {
      establishmentDate: string;
      employeeCount: string;
      capital: string;
      headquarters: string;
      ceo: string;
      businessModel: string;
    };
    businessMetrics?: {
      monthlyVolume: string;
      customerCount: string;
      growthRate: string;
      marketPosition: string;
      ipoStatus: string;
      fundingRound: string;
    };
    keyFeatures?: {
      creditLimit: string;
      approvalSpeed: string;
      integrations: string;
      annualFee?: string;
      pointReward?: string;
      support?: string;
      apiAvailability?: string;
    };
  };
  competitors?: Array<{
    id: string;
    name: string;
    type: string;
    description: string;
    lastChecked: string;
  }>;
}

const isFetchingStatus = (status: any) => {
  return status === 4 || status === '4' || status === 'fetching';
};

const getServiceStatusLabel = (status: ServiceData['status']) => {
  switch (status) {
    case 'active':
      return '稼働中';
    case 'fetching':
      return '取得中';
    case 'failed':
      return '取得失敗';
    case 'discontinued':
      return '廃止予定';
    default:
      return '停止中';
  }
};

// カテゴリ → アイコン/色（モックの商品リストカード準拠: 色付きタイル＋Material Icon）
const CATEGORY_VISUALS: Array<{ match: RegExp; icon: string; color: string }> = [
  { match: /サービス|ホテル|宿泊/, icon: 'apartment', color: '#616FC9' },
  { match: /SaaS|クラウド|ツール/i, icon: 'cloud', color: '#9675C9' },
  { match: /ヘルス|ビューティ|美容/, icon: 'spa', color: '#C9619C' },
  { match: /食品|飲料|フード/, icon: 'restaurant', color: '#E0894B' },
  { match: /オフィス|文具/, icon: 'work', color: '#4F86C6' },
  { match: /エレクトロニクス|家電/, icon: 'devices', color: '#44C2A4' },
];

const getCategoryVisual = (category: string): { icon: string; color: string } => {
  const found = CATEGORY_VISUALS.find((v) => v.match.test(category));
  if (found) return { icon: found.icon, color: found.color };
  return { icon: 'category', color: '#C9A061' };
};

// ナレッジ充実度（0-100）: 登録済みナレッジのブロック数から算出（モックの「充実度 N」表示）
const getCompletenessScore = (service: ServiceData): number => {
  const meta = service.productData?.extended_metadata;
  let score = 0;
  if (service.productData?.description) score += 10;
  if (service.productData?.url) score += 8;
  const imgs = service.productData?.product_images || service.productData?.images;
  if (imgs && imgs.length > 0) score += 14;
  if (meta?.product) score += 14;
  if (meta?.pricing_offer) score += 8;
  if (meta?.positioning) score += 14;
  if (meta?.who && meta.who.length > 0) score += 20;
  if (meta?.brand_guideline) score += 12;
  return Math.min(score, 100);
};

// 更新日を モック表記（MM-DD）に
const formatUpdatedShort = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}-${dd}`;
};

const getServiceStatusClass = (status: ServiceData['status']) => {
  switch (status) {
    case 'active':
      return 'bg-green-50 text-green-700';
    case 'fetching':
      return 'bg-warning/10 text-warning';
    case 'failed':
      return 'bg-red-50 text-red-600';
    case 'discontinued':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

/**
 * 商品情報の主タブ。
 * 「API連携」はタブ行の右端（TaskyTabs の trailing）に置くのでここには含めない。
 * 資料タブは一旦非表示（復活時は { value: '資料', label: '資料' } をここに戻す）。
 */
const KNOWLEDGE_TAB_ITEMS: TaskyTabItem[] = [
  { value: '戦略マップ', label: '戦略マップ' },
  { value: '基本情報', label: '基本情報' },
  { value: '商品画像', label: '商品画像' },
  { value: 'ブランド', label: 'ブランド' },
  { value: '競合情報', label: '競合情報' },
  // 広告連携は商品を作り込んだ後の設定なので一番右（9/25 定例の合意）。
  { value: '広告連携', label: '広告連携' },
];

const KnowledgeDashboard: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const enableApiIntegration = user?.organization?.enable_api_integration ?? false;
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('戦略マップ');
  const [isProductCreateModalOpen, setIsProductCreateModalOpen] = useState(false);
  const [isProductRegistrationModalOpen, setIsProductRegistrationModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationRecipients, setNotificationRecipients] = useState<NotificationRecipient[]>([]);

  // デバッグ用：モーダル状態の監視
  useEffect(() => {
    console.log('モーダル状態が変更されました:', isProductCreateModalOpen);
  }, [isProductCreateModalOpen]);

  // Fetch products from API
  const { products, loading: productsLoading, error: productsError, refreshProducts } = useProducts();

  // /home のオンボーディング（商品を登録する）から ?onboarding=1 で来た初回ユーザーに
  // 「まずは商品を登録してみましょう」案内モーダルを出す（商品0件の時だけ）。
  const [searchParams, setSearchParams] = useSearchParams();
  // `?tab=` は最初に開いた時点の値だけ使う（以後の URL 変更でタブを切り替えない）。
  const initialTabParam = useRef(searchParams.get('tab'));
  const [onboardingGuideDismissed, setOnboardingGuideDismissed] = useState(false);
  const [manualGuideOpen, setManualGuideOpen] = useState(searchParams.get('guide') === '1');
  const showOnboardingGuide = manualGuideOpen || shouldShowOnboardingGuide({
    onboardingParam: searchParams.get('onboarding') === '1',
    dismissed: onboardingGuideDismissed,
    loading: productsLoading,
    productCount: Array.isArray(products) ? products.length : null,
  });

  const dismissOnboardingGuide = useCallback(() => {
    setOnboardingGuideDismissed(true);
    setManualGuideOpen(false);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('onboarding');
        next.delete('guide');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // ABテスト一覧などから `?product=<id>` で来たら、その商品を選択状態にして開く。
  // 商品の読み込みが終わるまで（＝一覧に見つかるまで）null なので、初期選択より優先させても取りこぼさない。
  const productParamSelection = selectedServiceFromProductParam(searchParams.get('product'), products);
  const productParamAppliedRef = useRef(false);
  const productsLoadStartedRef = useRef(false);

  const hasFetchingProducts = products?.some((product) => isFetchingStatus(product.status)) ?? false;

  useEffect(() => {
    if (!hasFetchingProducts) {
      return;
    }

    const interval = window.setInterval(() => {
      // サイレント再取得: ローディング表示を出さず、内容に変化がある時だけ一覧を更新する
      // （5秒ごとの再取得で一覧がリセット・スケルトン表示されチラつくのを防ぐ）。
      refreshProducts({ silent: true });
    }, 5000);

    return () => window.clearInterval(interval);
  }, [hasFetchingProducts, refreshProducts]);

  // 商品が読み込まれたら最初の商品を選択
  useEffect(() => {
    if (products && products.length > 0 && !selectedService) {
      // `?product=<id>` 指定があるならそちらを優先する（下の effect が選ぶ）。
      // 先に先頭商品を入れてしまうと、一瞬別の商品が開いてから差し替わる
      if (productParamSelection && !productParamAppliedRef.current) {
        return;
      }
      // 最初の商品系サービスを選択
      const firstProduct = products[0];
      if (firstProduct) {
        setSelectedService(`product_${firstProduct.id}`);
      }
    }
  }, [products, selectedService, productParamSelection]);

  // `?product=<id>` は「商品一覧の初回読み込みが終わった時点」で1回だけ判定する。
  // その時点で見つからなければ以後は適用しない。成功時だけ ref を立てると、5秒ポーリングで
  // 後から現れた商品が、その間にユーザーが手で選んだ商品を上書きしてしまう。
  useEffect(() => {
    if (productsLoading) {
      productsLoadStartedRef.current = true;
      return;
    }
    // 読み込みが始まる前（初回レンダーの products=[] / loading=false）は判定しない
    if (!productsLoadStartedRef.current || productParamAppliedRef.current) {
      return;
    }
    productParamAppliedRef.current = true;
    if (productParamSelection) {
      setSelectedService(productParamSelection);
    }
  }, [productsLoading, productParamSelection]);

  // カテゴリマッピング - DBのカテゴリから表示用カテゴリに変換
  const getCategoryLabel = (category: string): string => {
    const categoryMap: Record<string, string> = {
      'Electronics': 'Electronics',
      'Fashion': 'Fashion',
      'Food & Beverage': 'Food & Beverage',
      'Home & Garden': 'Home & Garden',
      'Books & Media': 'Books & Media',
      'other': 'その他'
    };
    return categoryMap[category] || category;
  };

  // Productデータをサービス形式に変換
  const convertProductsToServices = (products: Product[]): ServiceData[] => {
    return products.map(product => {
      // カテゴリのデバッグログ - categoriesは配列なので最初の要素を取得
      // ①: カテゴリ未紐付けでも、URL抽出で得たカテゴリ名を一覧カードに表示する。
      const firstCategory =
        (product.categories && product.categories.length > 0 ? product.categories[0].name : null) ||
        product.extended_metadata?.product?.category ||
        null;
      console.log('Product category debug:', {
        productId: product.id,
        productName: product.name,
        rawCategories: product.categories,
        firstCategory: firstCategory,
        processedCategory: getCategoryLabel(firstCategory || 'other')
      });

      // ステータスを数値または文字列から統一された文字列に変換
      // APIから数値(1,2,3)または文字列('active','inactive')で来る可能性がある
      let status: ServiceData['status'] = 'inactive';

      // 型を無視して値をチェック
      const statusValue = product.status as any;

      if (statusValue === 1 || statusValue === 'active' || statusValue === '1') {
        status = 'active';
      } else if (statusValue === 4 || statusValue === 'fetching' || statusValue === '4') {
        status = 'fetching';
      } else if (statusValue === 5 || statusValue === 'failed' || statusValue === '5') {
        status = 'failed';
      } else if (statusValue === 3 || statusValue === 'discontinued' || statusValue === '3') {
        status = 'discontinued';
      } else if (statusValue === 2 || statusValue === 'inactive' || statusValue === '2') {
        status = 'inactive';
      }

      return {
        id: `product_${product.id}`,
        name: product.name,
        category: getCategoryLabel(firstCategory || 'other'),
        status: status,
        lastUpdated: new Date(product.updated_at).toLocaleDateString('ja-JP'),
        // Product固有のデータも保持
        productData: {
          id: product.id,
          status: product.status,
          url: product.url,
          description: product.description,
          price: product.price,
          salePrice: product.sale_price,
          currency: product.currency,
          stockQuantity: product.stock_quantity,
          images: product.images,
          product_images: product.product_images, // Knowledge\ProductImageテーブルからの画像
          tags: product.tags,
          metadata: product.metadata,
          extended_metadata: product.extended_metadata
        }
      };
    });
  };

  // staticServicesを削除：商品データのみを使用

  // 商品データのみを表示（staticServicesは除外）
  const services: ServiceData[] = [
    ...(products ? convertProductsToServices(products) : [])
  ];

  // 検索フィルタリング（①: カテゴリチップ列は場所を取りすぎるため削除。検索のみ残す）
  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentService = services.find(s => s.id === selectedService) || null;
  const previousSelectedService = useRef<string | null>(null);

  useEffect(() => {
    if (currentService) {
      console.log('[KnowledgeDashboard] currentService updated', {
        serviceId: currentService.id,
        productId: currentService.productData?.id,
        name: currentService.name,
      });
    } else {
      console.log('[KnowledgeDashboard] currentService cleared');
    }
  }, [currentService]);

  useEffect(() => {
    if (!selectedService) {
      return;
    }

    const isNewSelection = previousSelectedService.current !== selectedService;

    if (isNewSelection) {
      // 最初の選択だけ `?tab=` を見る（広告運用の画面から広告連携タブへ直接来る）。
      // 以後の商品切り替えは従来どおり戦略マップに戻す。
      const isInitialSelection = previousSelectedService.current === null;
      previousSelectedService.current = selectedService;
      setActiveTab(isInitialSelection ? knowledgeTabFromParam(initialTabParam.current) : '戦略マップ');
      // Reset notification recipients when switching products
      setNotificationRecipients([]);
    }
  }, [selectedService]);

  useEffect(() => {
    console.log('KnowledgeDashboard: Active tab changed', activeTab);
  }, [activeTab]);

  const handleDeleteClick = (product: Product, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setProductToDelete(product);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    try {
      await productService.deleteKnowledgeProduct(productToDelete.id);
      console.log('商品を削除しました');

      if (selectedService === `product_${productToDelete.id}`) {
        setSelectedService(null);
      }

      await refreshProducts();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      showToast(error?.response?.data?.message || error?.message || '商品の削除に失敗しました', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setProductToDelete(null);
    }
  };

  const competitorTargetProductId = currentService?.productData?.id;
  const competitorTargetProductName = currentService?.name || undefined;
  const canManageNotifications = user?.role === 'leader';
  const currentProduct = products?.find(p => p.id === currentService?.productData?.id) || null;
  const { isRefetching, startRefetch } = useProductRefetch({
    productId: currentProduct?.id || 0,
    onSuccess: refreshProducts,
    onError: (error) => showToast(error, 'error'),
  });

  const handleRefetchProduct = async () => {
    if (!currentProduct?.url) {
      showToast('URLが登録されていないため、情報を更新できません', 'error');
      return;
    }

    if (!(await confirm({ title: 'AIで商品情報を再取得します。よろしいですか？', confirmText: '再取得する' }))) {
      return;
    }

    await startRefetch();
  };

  const [generatingBrandGuideline, setGeneratingBrandGuideline] = useState(false);

  const handleBrandGuidelineUpdate = async (guideline: BrandGuideline) => {
    const productId = currentService?.productData?.id;
    if (!productId) return;

    try {
      await productService.updateBrandGuideline(productId, guideline);
      await refreshProducts();
    } catch (err: any) {
      console.error('Brand guideline update error:', err);
      showToast(err?.response?.data?.message || err?.message || 'ブランドガイドラインの更新に失敗しました', 'error');
    }
  };

  const handleBrandGuidelineGenerate = async () => {
    const productId = currentService?.productData?.id;
    if (!productId || generatingBrandGuideline) return;

    setGeneratingBrandGuideline(true);
    try {
      await productService.generateBrandGuideline(productId);
      await refreshProducts();
    } catch (err: any) {
      console.error('Brand guideline generate error:', err);
      showToast(err?.response?.data?.message || err?.message || 'ブランドガイドラインの生成に失敗しました', 'error');
    } finally {
      setGeneratingBrandGuideline(false);
    }
  };

  // 戦略マップのマトリクスは横に長く、右端で「切れている」ように見える（実際は横スクロールできる）。
  // まだ右に続きがあるときだけ、右端にグラデーションの手がかりを出す。
  // マトリクス本体（components/StrategyMatrix.tsx）は担当外のため、包むコンテナ側だけで扱う。
  // ref ではなく state で持つ（パネルがマウント／アンマウントした瞬間に effect を張り直すため）。
  const [strategyPanel, setStrategyPanel] = useState<HTMLDivElement | null>(null);
  const [hasStrategyOverflow, setHasStrategyOverflow] = useState(false);

  useEffect(() => {
    if (!strategyPanel) {
      setHasStrategyOverflow(false);
      return;
    }

    const update = () => {
      const scrollers: HTMLElement[] = [
        strategyPanel,
        ...Array.from(
          strategyPanel.querySelectorAll<HTMLElement>(
            '[class*="overflow-x-auto"], [class*="overflow-x-scroll"]',
          ),
        ),
      ];
      setHasStrategyOverflow(
        scrollers.some((el) => el.scrollWidth - el.clientWidth - el.scrollLeft > 4),
      );
    };

    update();
    // scroll はバブリングしないのでキャプチャで子孫の横スクロールを拾う。
    strategyPanel.addEventListener('scroll', update, true);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(strategyPanel);

    return () => {
      strategyPanel.removeEventListener('scroll', update, true);
      observer?.disconnect();
    };
  }, [strategyPanel]);

  const renderTabContent = () => {
    switch (activeTab) {
      case '競合情報':
        return (
          <CompetitorTab
            productId={competitorTargetProductId}
            productName={competitorTargetProductName}
          />
        );
      // 資料タブは一旦非表示（復活時はこのcaseとタブ行リンク・TabsContentのコメントを外す）
      // case '資料':
      //   return (
      //     <MaterialsTab
      //       productId={competitorTargetProductId}
      //       productName={competitorTargetProductName}
      //     />
      //   );
      case '広告連携':
        return <ProductAdAccountsTab productId={competitorTargetProductId} />;
      case 'API連携':
        return <APIIntegrationTab productId={competitorTargetProductId} />;
      case '戦略マップ':
        return <StrategyMapTab product={currentProduct} onProductUpdate={refreshProducts} />;
      case '基本情報':
        return (
          <BasicInfoTab
            product={currentProduct}
            onEdit={() => {
              // 既存の商品編集モーダル（ProductCreateForm）を開く。編集→保存で refreshProducts が走る。
              if (currentProduct) {
                setEditingProduct(currentProduct);
                setIsProductCreateModalOpen(true);
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  const sidebar = (
    <Sidebar
      onToggleCollapse={setSidebarCollapsed}
      collapsed={sidebarCollapsed}
    />
  );
  const mobileSidebar = <Sidebar />;

  return (
    <ResponsiveAppShell
      sidebar={sidebar}
      mobileSidebar={mobileSidebar}
      topbarTitle="商品情報"
      topbarActions={
        <>
          <TaskyButton
            size="sm"
            data-tutorial="knowledge-add"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsProductRegistrationModalOpen(true);
            }}
            aria-label="新規追加"
          >
            <Plus className="w-4 h-4" />
          </TaskyButton>
        </>
      }
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-white max-[640px]:min-w-0 max-[640px]:overflow-x-hidden lg:h-screen lg:min-h-0">
        <div className="hidden h-14 items-center px-5 bg-white flex-none lg:flex">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2.5">
              {sidebarCollapsed && (
                <button
                  type="button"
                  onClick={() => setSidebarCollapsed(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-tasky-sm text-gray-500 hover:bg-warm-hover hover:text-dark transition-colors"
                  title="サイドバーを展開"
                >
                  <Menu className="w-4 h-4" />
                </button>
              )}
              <Package className="w-5 h-5 text-primary" />
              <h1 className="text-lg font-semibold text-dark">商品情報</h1>
            </div>
            <div className="flex items-center gap-2">
              <TaskyHelpLink />
              <button type="button" onClick={() => setManualGuideOpen(true)} className="rounded-lg px-2 py-2 text-sm font-semibold text-primary hover:bg-primary/5">この画面の使い方</button>
              <TaskyButton
                size="sm"
                data-tutorial="knowledge-add"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('新規追加ボタンがクリックされました');
                  setIsProductRegistrationModalOpen(true);
                  console.log('ProductRegistrationModalを開きました');
                }}
              >
                <Plus className="w-4 h-4" />
                新規追加
              </TaskyButton>
            </div>
          </div>
        </div>

        <div className="border-b border-warm-border px-4 py-2 lg:hidden">
          <button type="button" onClick={() => setManualGuideOpen(true)} className="rounded-lg py-2 text-sm font-semibold text-primary">この画面の使い方</button>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden lg:overflow-hidden">
          <div className="flex min-h-full flex-col lg:h-full lg:flex-row">
            <div className="w-full border-b border-[#E8E6E0] flex flex-col bg-white lg:w-[300px] lg:flex-shrink-0 lg:border-b-0 lg:border-r">
              {/* Search and Filter */}
              <div className="p-4 bg-white">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" size={16} />
                  <input
                    type="text"
                    data-tutorial="knowledge-search"
                    placeholder="商品・サービスを検索..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-lg border-none bg-warm-surface py-2.5 pl-10 pr-4 text-sm text-dark placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/25"
                  />
                </div>
              </div>

              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Service Cards */}
                <div className="max-h-[42dvh] flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-white sm:px-5 lg:max-h-none" data-tutorial="knowledge-product-list">
                  {productsLoading && (
                    <div className="flex flex-col items-center justify-center gap-2 py-8 text-gray-500">
                      <TaskySpinner size="md" />
                      <span className="text-sm">商品データを読み込み中...</span>
                    </div>
                  )}
                  {productsError && (
                    <TaskyErrorState
                      title="データの読み込みに失敗しました"
                      description={productsError}
                      className="my-4"
                    />
                  )}
                  {filteredServices.map((service) => {
                    const isSelected = selectedService === service.id;

                    return (
                      <div
                        key={service.id}
                        onClick={() => {
                          const isDifferentSelection = selectedService !== service.id;

                          console.log('[KnowledgeDashboard] Service card clicked', {
                            serviceId: service.id,
                            productId: service.productData?.id,
                            serviceName: service.name,
                            wasDifferentSelection: isDifferentSelection,
                          });

                          setSelectedService(service.id);

                          if (isDifferentSelection || activeTab !== '戦略マップ') {
                            console.log('[KnowledgeDashboard] Switching active tab to 戦略マップ');
                            setActiveTab('戦略マップ');
                          }
                        }}
                        className={`group rounded-xl p-3.5 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-primary'
                            : 'bg-warm-surface hover:bg-warm-hover'
                        }`}
                      >
                        {/* DCMモック準拠: カテゴリ色のアイコンタイル + 名前/カテゴリ・充実度 + 下段ステータス行 */}
                        <div className="flex items-center gap-3">
                          {(() => {
                            const visual = getCategoryVisual(service.category || '');
                            const imgs = service.productData?.product_images || service.productData?.images || [];
                            const primary = imgs.find((img: any) => img?.is_primary) || imgs[0];
                            const imageUrl = primary?.url;
                            return (
                              <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg">
                                {imageUrl && (
                                  <img
                                    src={imageUrl}
                                    alt={service.name}
                                    // object-contain: 縦長/横長画像でも見切れず全体を表示。
                                    // 選択中カード（indigo背景）でも余白が視認できるよう bg-white を敷く
                                    className="h-full w-full rounded-lg bg-white object-contain"
                                    onError={(e) => {
                                      const img = e.currentTarget;
                                      img.style.display = 'none';
                                      const fb = img.nextElementSibling as HTMLElement | null;
                                      if (fb) fb.style.display = 'flex';
                                    }}
                                  />
                                )}
                                <div
                                  className="flex h-full w-full items-center justify-center rounded-lg"
                                  style={{
                                    display: imageUrl ? 'none' : 'flex',
                                    ...(isSelected
                                      ? { background: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }
                                      : { background: `${visual.color}26`, color: visual.color }),
                                  }}
                                >
                                  <span className="material-icons-round text-[20px]">{visual.icon}</span>
                                </div>
                              </div>
                            );
                          })()}
                          <div className="min-w-0 flex-1">
                            <div className={`truncate text-sm font-bold ${isSelected ? 'text-white' : 'text-dark'}`}>
                              {service.name}
                            </div>
                            <div className={`truncate text-[11px] ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>
                              {service.category} ・ 充実度 {getCompletenessScore(service)}
                            </div>
                          </div>
                          {service.productData && (
                            <button
                              className={`flex-shrink-0 rounded p-1 opacity-0 transition-all group-hover:opacity-100 ${
                                isSelected
                                  ? 'text-white/60 hover:bg-white/15 hover:text-white'
                                  : 'text-gray-400 hover:bg-destructive/10 hover:text-destructive'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const product = products?.find(p => p.id === service.productData?.id);
                                if (product) {
                                  handleDeleteClick(product, e);
                                }
                              }}
                              title="削除"
                              aria-label={`${service.name}を削除`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="mt-2.5 flex items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                              isSelected ? 'bg-white/20 text-white' : getServiceStatusClass(service.status)
                            }`}
                          >
                            {service.status === 'fetching' && <Clock className="h-3 w-3 animate-spin" />}
                            {service.status === 'failed' && <AlertCircle className="h-3 w-3" />}
                            {getServiceStatusLabel(service.status)}
                          </span>
                          <span className={`text-[10px] ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>
                            更新 {formatUpdatedShort(service.lastUpdated)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
            
            <div className="min-w-0 flex-1 flex flex-col bg-[#F8F7F3]">
              {currentService ? (
                currentProduct && isFetchingStatus(currentProduct.status) ? (
                  // ⑥: 取得処理中の商品は「触れない」進捗画面を表示する（タブ・編集は出さない）。
                  <div className="flex-1 overflow-y-auto">
                    <div className="border-b border-[#E8E6E0] bg-white px-4 py-4 sm:px-6">
                      <div className="flex items-center gap-3">
                        <h2 className="truncate text-xl font-semibold text-dark">{currentService.name}</h2>
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getServiceStatusClass(currentService.status)}`}
                        >
                          {getServiceStatusLabel(currentService.status)}
                        </span>
                      </div>
                    </div>
                    <KnowledgeFetchProgress
                      productId={currentProduct.id}
                      variant="panel"
                      onDone={refreshProducts}
                    />
                  </div>
                ) : (
                <div className="flex-1 overflow-hidden">
                  <div className="flex h-full flex-col">
                    <div className="border-b border-[#E8E6E0] bg-white px-4 pt-4 pb-0 sm:px-6">
                      <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                          <h2 className="truncate text-xl font-semibold text-dark">{currentService.name}</h2>
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getServiceStatusClass(currentService.status)}`}
                          >
                            {getServiceStatusLabel(currentService.status)}
                          </span>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                          <NotificationRecipientAvatars recipients={notificationRecipients} />
                          <button
                            type="button"
                            onClick={() => setIsNotificationModalOpen(true)}
                            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-[#AEB5DE] hover:bg-[#F5F6FC] hover:text-primary-hover sm:w-auto"
                          >
                            <Bell className="w-4 h-4 text-gray-500" />
                            通知先
                          </button>
                          <TaskyButton
                            size="sm"
                            onClick={handleRefetchProduct}
                            disabled={isRefetching || !currentProduct?.url}
                          >
                            {isRefetching ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Sparkles className="w-4 h-4" />
                            )}
                            {isRefetching ? '更新中...' : 'AIで情報を更新'}
                          </TaskyButton>
                        </div>
                      </div>

                      {/* -mb-px: TaskyTabs 自身の下線をヘッダの区切り線に重ねて二重線にしない */}
                      <div className="-mx-4 -mb-px overflow-x-auto px-4 sm:mx-0 sm:px-0" data-tutorial="knowledge-tabs">
                        {/* DCMモック準拠: タブはプレーン（ヘルプチップは各パネル見出し側に置く） */}
                        <TaskyTabs
                          items={KNOWLEDGE_TAB_ITEMS}
                          value={activeTab}
                          onChange={setActiveTab}
                          aria-label="商品情報"
                          className="w-full min-w-max"
                          trailing={
                            /* API連携（モック外の既存機能）はタブ行の右端に控えめに残す */
                            enableApiIntegration ? (
                              <TaskyButton
                                variant="ghost"
                                size="sm"
                                onClick={() => setActiveTab('API連携')}
                                className={activeTab === 'API連携' ? 'text-primary' : undefined}
                              >
                                API連携
                              </TaskyButton>
                            ) : undefined
                          }
                        />
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto overflow-x-hidden">
                      <div
                        role="tabpanel"
                        aria-label={activeTab}
                        className="m-0 w-full max-w-[1080px] mx-auto px-6 py-6 space-y-4"
                      >
                        {activeTab === '商品画像' ? (
                          <ProductImagesTab
                            currentService={currentService}
                            products={products || []}
                            onProductUpdate={refreshProducts}
                          />
                        ) : activeTab === 'ブランド' ? (
                          <BrandGuidelineSection
                            guideline={currentService?.productData?.extended_metadata?.brand_guideline}
                            onUpdate={handleBrandGuidelineUpdate}
                            onGenerate={
                              currentService?.productData?.url ? handleBrandGuidelineGenerate : undefined
                            }
                            generating={generatingBrandGuideline}
                          />
                        ) : activeTab === '戦略マップ' ? (
                          <div ref={setStrategyPanel} className="relative">
                            {renderTabContent()}
                            {hasStrategyOverflow && (
                              <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-warm-bg to-transparent" />
                            )}
                          </div>
                        ) : activeTab === 'API連携' ? (
                          enableApiIntegration ? renderTabContent() : null
                        ) : (
                          // 資料タブを復活させる場合は renderTabContent() の case をここで拾う
                          renderTabContent()
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-dark mb-2">商品情報</h3>
                    <p className="text-sm text-gray-500">左のリストから選択してください</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 商品詳細登録・編集モーダル */}
      <SimpleModal
        isOpen={isProductCreateModalOpen}
        onClose={() => {
          setIsProductCreateModalOpen(false);
          setEditingProduct(null);
        }}
        title={editingProduct ? '商品編集' : '商品詳細登録'}
      >
        <ProductCreateForm
          product={editingProduct}
          onSuccess={() => {
            setIsProductCreateModalOpen(false);
            setEditingProduct(null);
            refreshProducts();
          }}
          onCancel={() => {
            setIsProductCreateModalOpen(false);
            setEditingProduct(null);
          }}
        />
      </SimpleModal>

      {/* オンボーディング案内モーダル（/home の「商品を登録する」からの初回導線） */}
      <KnowledgeOnboardingGuideModal
        isOpen={showOnboardingGuide}
        onClose={dismissOnboardingGuide}
        onRegister={() => {
          dismissOnboardingGuide();
          setIsProductRegistrationModalOpen(true);
        }}
      />

      {/* 商品登録モーダル（新規作成） */}
      <ProductRegistrationModal
        isOpen={isProductRegistrationModalOpen}
        onClose={() => setIsProductRegistrationModalOpen(false)}
        onDetailedRegistration={() => {
          setEditingProduct(null);
          setIsProductCreateModalOpen(true);
        }}
        onSuccess={(productId) => {
          // ⑤: 登録直後・完了時に一覧を再取得し、新商品を選択状態にする（モーダルは閉じない）。
          refreshProducts();
          if (productId) {
            setSelectedService(`product_${productId}`);
          }
        }}
      />

      {/* 削除確認ダイアログ */}
      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        productName={productToDelete?.name || ''}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setProductToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
      />

      {/* 通知設定モーダル */}
      {competitorTargetProductId && (
        <NotificationSettingsModal
          isOpen={isNotificationModalOpen}
          onClose={() => setIsNotificationModalOpen(false)}
          productId={competitorTargetProductId}
          canManage={canManageNotifications}
          onRecipientsChange={setNotificationRecipients}
        />
      )}
    </ResponsiveAppShell>
  );
};

export default KnowledgeDashboard;
// Force rebuild 20251125151038
